import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import type {ExtendedBlock} from '@/services/kontent-ai/types'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import {spacing} from '@/products/flexible-web-toolkit/styles'
import {getContainerClass} from '../helpers'

export type RichTextContentItem = IContentItem<{
	content: Elements.RichTextElement
	textAlignment: Elements.MultipleChoiceElement
	tableAlignment: Elements.MultipleChoiceElement
	tabTitle: Elements.TextElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	snippetContainerResize: Elements.MultipleChoiceElement
	showTextDivider: Elements.MultipleChoiceElement
	tableTextAlignment: Elements.MultipleChoiceElement
	stretchImageFullWidthLargeScreens: Elements.MultipleChoiceElement
}>

export const RichTextBlock: ExtendedBlock<
	RichTextContentItem,
	{
		className?: string
		richTextRendererClassName?: string
	}
> = ({
	block,
	BlockMapper,
	page,
	className,
	richTextRendererClassName,
	...context
}) => {
	const componentSpacingChoice: keyof typeof spacing =
		(block.elements.snippetSpacingSpacing[0]?.codename as
			| keyof typeof spacing
			| undefined) ?? 'none'
	const componentSpacing = spacing[componentSpacingChoice]

	const containerSizingChoice: keyof typeof spacing | null =
		(block.elements.snippetContainerResize[0]?.codename as
			| keyof typeof spacing
			| undefined) ?? null
	const containerSize = getContainerClass(
		containerSizingChoice || 'none',
		'w-full'
	)

	const getTextAlignment = (alignment: string | undefined): string => {
		switch (alignment) {
			case 'left':
				return 'text-left'
			case 'center':
				return 'text-center'
			case 'right':
				return 'text-right'
			case 'justify':
				return 'text-justify'
			default:
				return 'text-center'
		}
	}

	const textAlignment = getTextAlignment(
		block.elements.textAlignment[0]?.codename
	)
	const tableTextAlignment = getTextAlignment(
		block.elements.tableTextAlignment[0]?.codename
	)
	const stretchImageFullWidth =
		Array.isArray(block.elements.stretchImageFullWidthLargeScreens) &&
		block.elements.stretchImageFullWidthLargeScreens[0]?.codename === 'yes'
			? '2xl:w-full'
			: ''

	return (
		<>
			<div
				className={clsx(
					'flex flex-col',
					componentSpacing,
					containerSize,
					textAlignment,
					className
				)}
				data-kontent-element-codename="content"
				style={{overflowWrap: 'break-word'}}
			>
				<RichTextRenderer
					BlockMapper={BlockMapper}
					className={richTextRendererClassName}
					element={block.elements.content}
					page={page}
					stretchImageFullWidth={stretchImageFullWidth}
					tableTextAlignment={tableTextAlignment}
					{...context}
				/>
			</div>
			{block.elements.showTextDivider[0]?.codename === 'yes' ? (
				<hr
					className="mt-6 border border-gray-300"
					data-kontent-element-codename="show_text_divider"
				/>
			) : null}
		</>
	)
}
